import { Link } from 'react-router-dom';

/**
 * One person, as a row that opens their page.
 *
 * A real `<a>` rather than a div with a click handler: Tab has to reach it, and
 * a person's page is a URL that survives a refresh, which is the whole reason
 * the design pushes a route instead of expanding a drawer.
 *
 * The same component draws the account holder's card and a linked athlete's
 * row, because they open the same route and the same page. `lead` is the only
 * difference — the holder's name is set larger, being the card at the top.
 */
export function PersonRow({
  clientId,
  name,
  detail,
  tag,
  lead = false,
}: {
  clientId: string;
  name: string;
  detail?: string;
  /** Something needing attention, e.g. an unsigned waiver. Drawn in --warn. */
  tag?: string;
  lead?: boolean;
}) {
  return (
    <Link className={lead ? 'person-row lead' : 'person-row'} to={`/account/people/${clientId}`}>
      <span className="person-row-text">
        <span className="name">{name}</span>
        {detail ? <span className="detail">{detail}</span> : null}
      </span>
      {tag ? <span className="row-tag">{tag}</span> : null}
      <span className="chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

/** A date of birth as a person would say it, not as the database stores it. */
export function bornOn(dateOfBirth: string | null): string {
  if (!dateOfBirth) return '';
  // Parsed as UTC and formatted in UTC: a birthday read in the browser's zone
  // shows the day before for anyone west of Greenwich.
  const date = new Date(`${dateOfBirth}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateOfBirth;

  return `Born ${date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })}`;
}
